import React, { useEffect, useState } from "react";

import { graphql, useStaticQuery } from "gatsby";

import Card from "components/others/card";

import {
  filterEdgesByBothTag,
  filterEdgesByTag,
  filterEdgesByTechnologyTitle,
  limitEdges,
  removeEdgesBySlug,
} from "utils/edgesFilter";
import { isOpenSourceTheme } from "utils/themeFilter";

interface ThemeSectionProps {
  tag?: string;
  title?: string;
  excludeSlug?: string;
  technologiesTitle?: string;
  limit?: number;
  filterByBothTag?: boolean;
  blogDetailsRelatedPosts?: boolean;
}

//* USAGE guide: (all props are optional)
//* <ThemeSection/> => will call all theme data, sorted by descending 'updated_date'
//* <ThemeSection limit={3} /> => will show first three Themes
//* <ThemeSection tags="TAG_NAME"/> => will show filter all data by tag="TAG_NAME" (technologies filter is same)
//* <ThemeSection tags="TAG_NAME" limit={3} /> => will show 3 themes with tag="TAG_NAME" and force to have 3 theme. (IMPORTANT: IF there are no three themes, with such tag, will append data with tag="Popular")
const ThemeSection: React.FC<ThemeSectionProps> = ({
  tag,
  excludeSlug,
  technologiesTitle,
  limit,
  filterByBothTag,
  blogDetailsRelatedPosts
}) => {
  // * Fetch All data in descending order (date), So by default data is sorted by latest
  // TO-DO: define the type of data to fetch
  const themeItems = useStaticQuery(
    graphql`
      query MyQuery {
        allMdx(
          filter: { frontmatter: { type: { eq: "template" } } }
          sort: { frontmatter: { updatedAt: DESC } }
        ) {
          edges {
            node {
              frontmatter {
                author
                authorImage {
                  childImageSharp {
                    resize {
                      src
                    }
                  }
                }
                featuredImage {
                  childImageSharp {
                    fluid(maxWidth: 600) {
                      src
                    }
                  }
                }
                slug
                previewUrl
                purchaseInfo {
                  url
                  directLink
                }
                tags
                technologies {
                  title
                  class
                }
                title
                price
                comparePrice
                gumroadId
                mainTechnology
                downloadDetails {
                  source
                  url
                  branch
                }
                ribbonContent
              }
            }
          }
        }
        shopifyThemes: allMdx(
            filter: {frontmatter: {author: {eq: "Polo Themes"}, mainTechnology: {eq: "Shopify"}}}
            sort: { frontmatter : { orderId: ASC }}
          ) {
            edges {
              node {
                frontmatter {
                  title
                  author
                  tags
                  slug
                  technologies {
                    title
                    class
                  }
                  featuredImage {
                    childImageSharp {
                      fluid(maxWidth: 600) {
                        src
                      }
                    }
                  }
                  price
                  gumroadId
                  ribbonContent
                  comparePrice
                  previewUrl
                  purchaseInfo {
                  url
                  directLink
                }
              }
            }
          }
        }
      }
    `
  );
const [relatedOpenSourceThemes, setRelatedOpenSourceThemes] = useState([])

  const themeData = themeItems.allMdx.edges;
  let filteredData = themeData; // * Default data (for: no props)

  const shopifyThemesData = themeItems.shopifyThemes.edges.filter(
    (item: any) => item.node.frontmatter.tags.length === 1
  );
  
  useEffect(() => {
    const relatedOpenSourceThemes = isOpenSourceTheme(
      excludeSlug,
      blogDetailsRelatedPosts,
      themeData,
      shopifyThemesData
    );
  
    setRelatedOpenSourceThemes(relatedOpenSourceThemes);
  }, []);

  if (tag == "bundle-sf") {
    filteredData = filterEdgesByBothTag(filteredData, tag);
  }

  if (tag && !filterByBothTag) {
    filteredData = filterEdgesByTag(filteredData, tag, shopifyThemesData);
  }

  if (technologiesTitle) {
    filteredData = filterEdgesByTechnologyTitle(
      filteredData,
      technologiesTitle
    );
  }

  if (excludeSlug) {
    filteredData = removeEdgesBySlug(filteredData, excludeSlug);
  }

  if (limit && filteredData.length > limit) {
    filteredData = limitEdges(limit, filteredData, themeData);
  }

  if (tag == "Open-Source") filteredData = relatedOpenSourceThemes;

  return (
    <>
      {filteredData.map((edge: any, index: any) => {
        const cardObject = {
          name: edge.node.frontmatter.title,
          featuredImage:
            edge.node.frontmatter.featuredImage.childImageSharp.fluid.src,
          technologies: edge.node.frontmatter.technologies,
          author: edge.node.frontmatter.author,
          type: edge.node.frontmatter.type,
          price: edge.node.frontmatter.price,
          comparePrice: edge.node.frontmatter.comparePrice,
          url: edge.node.frontmatter.slug,
          previewUrl: edge.node.frontmatter.previewUrl,
          purchaseInfo: edge.node.frontmatter.purchaseInfo,
          title: edge.node.frontmatter.title,
          gumroadId: edge.node.frontmatter.gumroadId,
          downloadDetails: edge.node.frontmatter.downloadDetails,
          mainTechnology: edge.node.frontmatter.mainTechnology,
          ribbonContent: edge.node.frontmatter.ribbonContent,
        };

        return <Card {...cardObject} key={index} />;
      })}
    </>
  );
};

export default ThemeSection;
